// =====================================================================================
// useEditModeSocket - WebSocket communication for decoration edit mode
// =====================================================================================
// Handles sending staged changes to the server and receiving decoration updates
// from other users. Extracted from EditModeContext for separation of concerns.

import { useState, useCallback, useEffect } from 'react';
import { useAppContext } from './useAppContext.js';
import type {
  DecorationChange,
  UpdateDecorationsRequest,
} from '@shared/schemas/decorations.schema.js';

/**
 * Hook that manages WebSocket communication for decoration editing.
 * Handles committing staged changes and receiving remote decoration updates.
 */
export function useEditModeSocket(
  stagedChanges: DecorationChange[],
  setStagedChanges: React.Dispatch<React.SetStateAction<DecorationChange[]>>,
  localDecorationIds: React.MutableRefObject<Set<number>>,
  getGameScene: () => any
): {
  commitChanges: () => Promise<boolean>;
  isLoading: boolean;
  error: string | null;
  setError: (error: string | null) => void;
} {
  const { roomConnection } = useAppContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Commit all staged changes
  const commitChanges = useCallback(async () => {
    if (stagedChanges.length === 0 || !roomConnection.socket || !roomConnection.currentRoom) {
      if (!roomConnection.currentRoom) {
        setError('No room selected');
      }
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const request: UpdateDecorationsRequest = {
        roomId: roomConnection.currentRoom.id,
        changes: stagedChanges
      };

      // Send changes to backend
      // Note: we don't clear decorations here -- the server's `decorations:updated`
      // broadcast (which includes the sender) handles clearing and reloading.
      roomConnection.socket.emit('decorations:update', request);

      // Wait for response (with timeout)
      await new Promise<void>((resolve, reject) => {
        const handleResponse = (response: { success?: boolean; message?: string; changes?: DecorationChange[] }) => {
          clearTimeout(timeout);
          roomConnection.socket?.off('decorations:update_response', handleResponse);

          if (response.success) {
            resolve();
          } else {
            reject(new Error(response.message || 'Failed to update decorations'));
          }
        };

        const timeout = setTimeout(() => {
          roomConnection.socket?.off('decorations:update_response', handleResponse);
          reject(new Error('Update request timed out'));
        }, 10000);

        roomConnection.socket?.on('decorations:update_response', handleResponse);
      });

      // Clear staged changes and local tracking on success
      setStagedChanges([]);
      localDecorationIds.current = new Set();
      return true;

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to commit changes';
      setError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [stagedChanges, roomConnection.socket, roomConnection.currentRoom, setStagedChanges, localDecorationIds]);

  // Handle decoration updates from other users
  useEffect(() => {
    const socket = roomConnection.socket;
    if (!socket) return;

    const handleDecorationUpdate = async (data: UpdateDecorationsRequest) => {
      const scene = getGameScene();
      if (!scene || !scene.systems?.decorations) {
        return;
      }

      try {
        // Extract the decorations layer from the update message
        const decorationsLayer = data.data?.decorationsLayer || data.decorationsLayer;

        if (!decorationsLayer) {
          return;
        }

        // Get tilesets from the tilemap cache (same as GameScene does)
        const tilemapData = scene.cache.json.get('main_island_raw');
        const tilesets = tilemapData?.tilesets;

        // Clear existing decorations (but keep the system initialized)
        scene.systems.decorations.clearDecorations();

        // Reload decorations with the new layer data
        await scene.systems.decorations.loadDecorationsFromRoom(decorationsLayer, tilesets);

        // Update the collision map (method is on the scene, not pathfinder)
        if (scene.updateCollisionMap) {
          scene.updateCollisionMap();
        }

        // Update the room data in registry so it stays in sync
        const currentRoomData = scene.registry.get('currentRoomData');
        if (currentRoomData) {
          currentRoomData.decorationsLayer = decorationsLayer;
          scene.registry.set('currentRoomData', currentRoomData);
        }
      } catch (err) {
        console.warn('[EditMode] Failed to update decorations from server:', err);
      }
    };

    socket.on('decorations:updated', handleDecorationUpdate);

    return () => {
      socket.off('decorations:updated', handleDecorationUpdate);
    };
  }, [roomConnection.socket, getGameScene]);

  return { commitChanges, isLoading, error, setError };
}
